import {
  Survey,
  SurveyAnswers,
  SurveyAnswersSimple,
  SurveyElement,
  SurveyElementChoices,
  SurveyElementDropdown,
  SurveyElementRegions,
  SurveyElementValidator,
  SurveyPage,
  SurveyPageProcessorCommand,
  SurveyValueType
} from '../SurveyModels'
import {SurveyElementValidatorService} from 'core/builder/SurveyElementValidatorService'
import {SurveyNavigator} from 'core/renderer/SurveyNavigator'
import {SurveyAnswersMapper} from 'services/SurveyAnswersMapper'
import {SurveySessionStorage} from '../renderer/SurveySessionStorage'
import {SurveyAnalytics, SurveyAnalyticsLog} from './SurveyAnalytics'
import {SurveyPageProcessorFactory, SurveyPageProcessorMeta} from 'core/builder'

export class SurveyRendererValidationError extends Error {
  constructor(
    message: string,
    public inputs: string[]
  ) {
    super(message)
  }
}

export class SurveyRendererService {
  private readonly validator = new SurveyElementValidatorService()
  private navigator!: SurveyNavigator
  private survey!: Survey

  constructor(
    private baseSurvey: Survey,
    private templates?: Survey[],
    private sessionStorage?: SurveySessionStorage,
    private analytics?: SurveyAnalytics,
    private processor: SurveyPageProcessorFactory = new SurveyPageProcessorFactory()
  ) {
    this.setupSurvey()
    this.setupNavigator()
  }

  private setupSurvey() {
    this.survey = SurveyRendererService.buildSurvey(
      this.baseSurvey,
      this.templates
    )
    if (this.sessionStorage) {
      this.survey = new SurveyAnswersMapper(
        this.survey,
        this.sessionStorage.get()
      ).mergedSurveyAnswersCopy()
    }
  }

  private setupNavigator() {
    this.navigator = new SurveyNavigator(
      this.survey,
      this.getTemplateStartingIndex()
    )
  }

  public getTemplateStartingIndex() {
    const startingTemplatesOffset = this.templates
      ?.filter((t) => t.isStartingTemplate)
      .reduce((c, p) => c + p.pages.length, 0)
    const templateStartIndex = this.baseSurvey.pages.length

    return startingTemplatesOffset
      ? templateStartIndex + startingTemplatesOffset
      : templateStartIndex
  }

  static buildSurvey(survey: Survey, templates?: Survey[]) {
    const {startingTemplates, endingTemplates} =
      SurveyRendererService.extractTemplates(templates)

    const pages = [...startingTemplates, ...survey.pages, ...endingTemplates]
    return {...survey, pages}
  }

  static extractTemplates(templates?: Survey[]) {
    const startingTemplates: SurveyPage[] = []
    const endingTemplates: SurveyPage[] = []

    templates?.reverse().forEach((template) => {
      if (template.isStartingTemplate) {
        startingTemplates.push(...template.pages)
      } else {
        endingTemplates.push(...template.pages)
      }
    })
    return {startingTemplates, endingTemplates}
  }

  getQuestionNumber(element: SurveyElement) {
    return this.navigator.getQuestionNumber(element.name)
  }

  getBuildedSurvey() {
    return this.survey
  }

  getInvalidInputsForPage(index: number) {
    return this.survey.pages[index].elements
      .filter((el) => {
        if (
          !this.navigator.shouldRenderInput(
            this.survey.pages[index].name,
            el.name
          )
        ) {
          // skip hidden elements
          return false
        }
        if (el.validation && !!el.value) {
          return !this.validator.validate(el.validation, el.value)
        }
        if (el.required) {
          return Array.isArray(el.value) ? el.value.length === 0 : !el.value
        }
        return false
      })
      .map((el) => el.name)
  }

  updateValues(pages: Record<string, Record<string, SurveyValueType>>) {
    Object.keys(pages).forEach((page) => {
      Object.keys(pages[page]).forEach((element) => {
        const [pageIndex, elementIndex] = this.findElementIndex(page, element)
        if (pageIndex !== -1 && elementIndex !== -1) {
          this.survey.pages[pageIndex].elements[elementIndex].value =
            pages[page][element]
        }
      })
    })
    this.sessionStorage?.set(this.getAnswers())
    return this.getUpdatedPage()
  }

  validate(type: SurveyElementValidator, value: SurveyValueType) {
    return this.validator.validate(type, value)
  }

  // Simple will mean to return only values without otherValue
  getAnswers(): SurveyAnswers
  getAnswers(simple: boolean): SurveyAnswersSimple
  getAnswers(simple?: boolean): SurveyAnswersSimple | SurveyAnswers {
    const answers: Record<string, Record<string, SurveyValueType>> = {}
    this.survey.pages.forEach((page) => {
      answers[page.name] = {}
      page.elements.forEach((element) => {
        // Add only elements with values
        if (
          (!Array.isArray(element.value) && element.value) ||
          (Array.isArray(element.value) && element.value.length > 0)
        ) {
          answers[page.name][element.name] = simple
            ? element.value
            : {
                value: element.value,
                otherValue: (element as SurveyElementChoices).otherValue
              }
        }
      })
      // If current page had no inputs remove page from answers
      if (Object.keys(answers[page.name]).length === 0) {
        delete answers[page.name]
      }
    })
    return answers
  }

  getPages(): SurveyPage[] {
    return this.survey.pages.map((page) => ({
      ...page,
      elements: page.elements.filter((el) =>
        this.navigator.shouldRenderInput(page.name, el.name)
      )
    }))
  }

  getUpdatedPage(): SurveyPage {
    const fullPage = this.survey.pages[this.navigator.getPageIndex()]
    return {
      ...fullPage,
      elements: fullPage?.elements?.filter((el) =>
        this.navigator.shouldRenderInput(fullPage.name, el.name)
      )
    }
  }

  hasPageRequiredElements(pageName: string) {
    const elements = this.survey.pages.find(
      (page) => page.name === pageName
    )?.elements
    if (elements !== undefined) {
      return elements.some(
        (e) => (e.validation !== undefined && !!e.value) || e.required
      )
    }
    return false
  }

  freeShared() {
    SurveyRendererService.instance = undefined
  }

  getStartingPageIndex() {
    return this.navigator.getStartingPageIndex()
  }

  getCurrentPageIndex() {
    return this.navigator.getPageIndex()
  }

  private log(pageIndex: number, direction: SurveyAnalyticsLog['direction']) {
    if (!this.analytics) return
    const page = this.survey.pages[pageIndex]
    Promise.allSettled(
      page.elements.map((element) =>
        this.analytics?.log({
          direction: direction,
          pageId: page.name,
          question: element
        })
      )
    ).then()
  }

  async nextPageIndex(): Promise<number> {
    const index = this.navigator.getPageIndex()
    const invalidInputs = this.getInvalidInputsForPage(index)
    if (invalidInputs.length > 0) {
      throw new SurveyRendererValidationError('Invalid values', invalidInputs)
    }

    if (this.survey.pages[index]?.postProcessor) {
      const response = await this.executeProcessor(
        this.survey.pages[index].postProcessor!,
        {pageIndex: index}
      )
      if (!response.canProceed) {
        return this.navigator.getPageIndex()
      }
    }

    const nextIndex = this.navigator.nextPageIndex()
    // CLEAR ALL VALUES BETWEEN JUMPED PAGES
    for (let i = index + 1; i < nextIndex; i++) {
      const page = this.survey.pages[i]
      const data: Record<string, Record<string, SurveyValueType>> = {
        [page.name]: {}
      }
      page.elements.forEach((el) => {
        if (!el.prefilled) {
          data[page.name][el.name] = undefined
        }
      })
      this.updateValues(data)
    }

    if (this.survey.pages[nextIndex]?.preProcessor) {
      const response = await this.executeProcessor(
        this.survey.pages[nextIndex].preProcessor!,
        {pageIndex: nextIndex}
      )
      if (!response.canProceed) {
        return this.navigator.getPageIndex()
      }
    }
    this.log(index, 'NEXT')
    return nextIndex
  }

  // This will execute a specific command and will update survey object
  async executeProcessor(
    cmd: SurveyPageProcessorCommand,
    meta: SurveyPageProcessorMeta
  ) {
    const response = await this.processor.process(cmd, this.survey, meta)
    this.survey = response.survey
    return response
  }

  async previousPageIndex(): Promise<number> {
    this.log(this.navigator.getPageIndex(), 'BACK')
    return this.navigator.previousPageIndex()
  }

  canAutoAdvance(pageName: string): boolean {
    if (this.survey.autoAdvance) {
      const page = this.survey.pages.find((page) => page.name === pageName)
      const notSuitable = page?.elements
        .filter((el) => this.navigator.shouldRenderInput(pageName, el.name))
        .find(
          (el) => !SurveyRendererService.isElementSuitableForAutoAdvance(el)
        )
      return !notSuitable
    }
    return false
  }

  private static isElementSuitableForAutoAdvance(element: SurveyElement) {
    switch (element.type) {
      case 'choices':
        // If is single choice and is not other
        return (
          !(element as SurveyElementChoices).multiple &&
          element.value !== 'other' &&
          element.value
        )
      case 'dropdown':
        return !(element as SurveyElementDropdown).multiple && element.value
      // https://smarty.atlassian.net/browse/FIND-502
      // case 'regions':
      //   return !(element as SurveyElementRegions).multiple && element.value
      case 'datepicker':
        return !(element as SurveyElementRegions).multiple && element.value
      case 'time-picker':
        return element.value !== 'other' && element.value
      case 'budget':
        return element.value && element.value === -1
      case 'location-type':
        return !!element.value
      default:
        return false
    }
  }

  private findPageIndex(pageName: string) {
    return this.survey.pages.findIndex((page) => page.name === pageName)
  }

  private findElementIndex(forPage: string, elementName: string) {
    const pageIndex = this.findPageIndex(forPage)
    if (pageIndex === -1) {
      return [-1, -1]
    }
    return [
      pageIndex,
      this.survey.pages[pageIndex].elements.findIndex(
        (element) => element.name === elementName
      )
    ]
  }

  private static instance?: SurveyRendererService

  static shared(
    survey: Survey,
    templates?: Survey[],
    sessionStorage?: SurveySessionStorage,
    surveyAnalytics?: SurveyAnalytics,
    surveyPageProcessorFactory?: SurveyPageProcessorFactory
  ) {
    if (!SurveyRendererService.instance) {
      SurveyRendererService.instance = new SurveyRendererService(
        survey,
        templates,
        sessionStorage,
        surveyAnalytics,
        surveyPageProcessorFactory
      )
    }
    return SurveyRendererService.instance
  }
}
